import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import NestedMenuItem from '../FilterSelect/NestedMenuItem';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import { IconSvg } from 'components/atoms';
import FormHelperText from '@material-ui/core/FormHelperText';
import Bashkia from '../../../assets/icons/Statistics/bashkite.svg';
import Viti from '../../../assets/icons/Statistics/viti-01.svg';
import Periudha from '../../../assets/icons/Statistics/periudha-01.svg';
import LlojiTeDhenave from '../../../assets/icons/Statistics/te-dhenat.svg';

const images = { Bashkia, Viti, Periudha, LlojiTeDhenave };

const useStyles = makeStyles((theme) => ({
  select: {
    borderRadius: 999,
  },
  selectRoot: {
    padding: theme.spacing(1),
    fontFamily: 'Roboto',
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    boxShadow: '0px 3px 6px #00000029',
    borderRadius: '999px',
    '&:focus': {
      borderRadius: '999px',
    },
  },
  fromStatisticsWidth: {
    width: '250px',
  },
  secondStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    borderRadius: '999px',
  },
  selectText: {
    paddingRight: '50px!important',
  },
  menuListItem: {
    padding: 0,
  },
  removeTop: {
    top: '16px !important',
  },
  icon: {
    backgroundColor: '#293a42',
    color: 'white',
    borderRadius: '0 50% 50% 0',
    width: '1.6em',
    height: '35px',
    top: '-0.4px',
    right: '0!important',
  },
  selectIcon: {
    color: 'black',
  },
}));

const SimpleSelect = (props) => {
  const {
    items,
    placeholder,
    startText,
    name,
    multiple,
    value,
    onChange,
    secondStyle,
    fromStatistics,
    icon,
    error,
    filterTypeError,
    imageName,
    ...others
  } = props;
  const classes = useStyles();

  const [selected, setSelected] = React.useState(value);
  const { t } = useTranslation();

  const handleChange = (event) => {
    setSelected(event.target.value);
    if (onChange) onChange(event);
  };

  const [menuPosition, setMenuPosition] = React.useState(null);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const handleRightClick = (event) => {
    if (menuPosition) {
      return;
    }
    event.preventDefault();
    setMenuPosition({
      top: event.pageY,
      left: event.pageX,
    });
  };

  const handleItemClick = (event) => {
    handleChange(event);
    setMenuPosition(null);
  };

  return (
    <FormControl>
      <Select
        {...others}
        open={Boolean(menuPosition)}
        name={name}
        value={selected || ''}
        multiple={false}
        displayEmpty
        variant={/* !secondStyle ? 'outlined' :  */ 'standard'}
        className={classes.select}
        onOpen={handleRightClick}
        disableUnderline
        renderValue={() => {
          if (selected?.length === 0 || !selected) {
            return (
              <Grid container>
                {fromStatistics ? (
                  <>
                    <Grid item xs={1} style={{ margin: 'auto 0' }}>
                      {imageName ? (
                        <img src={images[imageName]} alt={imageName} />
                      ) : (
                        <IconSvg name={icon} viewBox='0 0 21.397 17.342' className={classes.icon} />
                      )}
                    </Grid>
                    <Grid item xs={10} style={{ margin: 'auto 6px', fontWeight: '500', fontSize: '14px' }}>
                      <span>{placeholder}</span>
                    </Grid>
                  </>
                ) : secondStyle ? (
                  <span>{placeholder}</span>
                ) : (
                  <em>{placeholder}</em>
                )}
              </Grid>
            );
          }

          const getSelectedItems = (arrayItems) => {
            return arrayItems.reduce((acc, curr) => {
              return [
                ...acc,
                ...(curr.children
                  ? getSelectedItems(curr.children)
                  : Array.isArray(selected)
                  ? selected.includes(curr.value)
                    ? [curr]
                    : [undefined]
                  : curr.value === selected
                  ? [curr]
                  : [undefined]),
              ].filter((x) => x);
            }, []);
          };

          const selectedItems = getSelectedItems(items);

          return (
            <Grid container>
              {fromStatistics ? (
                <>
                  <Grid item xs={1} style={{ margin: 'auto 0' }}>
                    {imageName ? (
                      <img src={images[imageName]} alt={imageName} />
                    ) : (
                      <IconSvg name={icon} viewBox='0 0 21.397 17.342' className={classes.icon} />
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={10}
                    style={{
                      margin: 'auto 6px',
                      fontWeight: '500',
                      fontSize: '14px',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {[startText || '', ': ', selectedItems.map((i) => i.description).join(', ')].join('')}
                  </Grid>
                </>
              ) : secondStyle ? (
                <span>{[startText || '', ': ', selectedItems.map((i) => i.description).join(', ')].join('')}</span>
              ) : (
                <em>{[startText || '', ': ', selectedItems.map((i) => i.description).join(', ')].join('')}</em>
              )}
            </Grid>
          );
        }}
        classes={{
          root: clsx(
            classes.selectRoot,
            secondStyle && classes.secondStyle,
            fromStatistics && classes.fromStatisticsWidth,
          ),
          select: secondStyle && classes.selectText,
          icon: (secondStyle && classes.icon) || classes.selectIcon,
        }}
        MenuProps={{
          open: !!menuPosition,
          onClose: () => setMenuPosition(null),
          // onSelect: () => setMenuPosition(null),
          anchorPosition: menuPosition,
          PaperProps: {
            className: window.innerHeight < items.length * 44 ? classes.removeTop : null,
          },
        }}
      >
        <MenuItem value={undefined}>{secondStyle ? <span>{placeholder}</span> : <em>{placeholder}</em>}</MenuItem>

        {items?.map((item) => {
          if (item.children && item.children.length)
            return (
              <NestedMenuItem key={item.value} label={item.description} parentMenuOpen={!!menuPosition}>
                {item.children?.map((subItem) => (
                  <MenuItem
                    key={subItem.value}
                    value={subItem.value}
                    onClick={(e) =>
                      handleItemClick({
                        ...e,
                        target: {
                          ...e.target,
                          value: subItem.value,
                        },
                        selectedItem: { parent: item, ...subItem },
                      })
                    }
                  >
                    {subItem.description}
                  </MenuItem>
                ))}
              </NestedMenuItem>
            );
          else
            return (
              <MenuItem
                key={item.value}
                value={item.value}
                onClick={(e) =>
                  handleItemClick({
                    ...e,
                    target: { ...e.target, value: item.value },
                    selectedItem: { ...item },
                  })
                }
              >
                {item.description}
              </MenuItem>
            );
        })}
      </Select>
      {error && <FormHelperText>{t('STATISTICS.REQUIRED_FIELD')}</FormHelperText>}
      {filterTypeError && <FormHelperText>{t('STATISTICS.REQUIRED_FILTER_TYPE')}</FormHelperText>}
    </FormControl>
  );
};

SimpleSelect.propTypes = {
  ...Select.propTypes,
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Placeholder
   */
  placeholder: PropTypes.string,
  /**
   * Text to show before the selected value on input
   */
  startText: PropTypes.string,
  /**
   * Item source data as shaped value | description
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      description: PropTypes.string.isRequired,
    }),
  ).isRequired,

  multiple: PropTypes.bool,
};

export default SimpleSelect;
